import { http } from '@pendo/http';
import isEmpty from 'lodash/isEmpty';
import toPairs from 'lodash/toPairs';
import uniq from 'lodash/uniq';
import has from 'lodash/has';
import { v4 as uuid } from 'uuid';
import { formatValueForSchema } from '@/utils/formatters';
import { useMetadataStore } from '@/stores/metadata.store';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import { i18n_t as $t } from '@/utils/i18n';
import { getComputedColor } from '@/utils/color';

export const METADATA_GROUP_COLORS = {
    agent: getComputedColor('--text-body-subtle-base'),
    salesforce: getComputedColor('--status-color-map-blue-primary'),
    segmentio: getComputedColor('--status-color-map-green-primary'),
    sfdc_user: getComputedColor('--status-color-map-blue-primary'),
    custom: getComputedColor('--status-color-map-purple-primary'),
    pendo_hubspot: getComputedColor('--status-color-map-orange-primary'),
    unknown: getComputedColor('--text-body-muted-base'),
};

export const METADATA_TYPE = {
    TEXT: 'string',
    BOOLEAN: 'boolean',
    INTEGER: 'integer',
    FLOAT: 'float',
    DATE: 'time',
    LIST: 'list',
};

export const ALL_TYPES = [
    {
        label: 'Text',
        value: METADATA_TYPE.TEXT,
    },
    {
        label: 'Boolean',
        value: METADATA_TYPE.BOOLEAN,
    },
    {
        label: 'Number (int)',
        value: METADATA_TYPE.INTEGER,
    },
    {
        label: 'Number (float)',
        value: METADATA_TYPE.FLOAT,
    },
    {
        label: 'Date',
        value: METADATA_TYPE.DATE,
    },
    {
        label: 'List',
        value: METADATA_TYPE.LIST,
    },
];

export function getMetadataTypeLabel (type, elementType) {
    const typeLabel = ALL_TYPES.find((entry) => entry.value === type)?.label || type || '';
    if (type !== METADATA_TYPE.LIST || !elementType) {
        return typeLabel;
    }

    const elementLabel = ALL_TYPES.find((entry) => entry.value === elementType)?.label || elementType;

    return $t('dataMappings.{baseType} of {elementType}', { baseType: typeLabel, elementType: elementLabel });
}

// supports query parameter `editableOnly=true` which includes metadata groups `agent`, `custom`, `salesforce`, `segmentio`
// and excludes groups `auto` and `pendo` and any hidden or deleted fields
export function fetchMetadataByKind (kind, params, raw = false) {
    return http
        .get(`api/s/_SID_/metadata/${kind}/schema`, { params })
        .then((response) => (raw === true ? response : processResponse(response)));
}

function processResponse (response) {
    const { data } = response;

    return toPairs(data).reduce((collection, [group, fieldMap]) => {
        return collection.concat(
            toPairs(fieldMap).map(([field, fieldObj]) => {
                fieldObj.group = group;
                fieldObj.field = field;

                return fieldObj;
            }),
        );
    }, []);
}

export function getWhitelistedGroups (groupsByKind, options) {
    options = { editableOnly: true, ...options };
    const { editableOnly, excludeDynamicGroups, includeTrackEventData, groupsArray, whitelist } = options;
    const defaultGroups = ['auto', 'agent', 'custom', 'salesforce', 'segmentio'];
    let defaultGroupBlacklist = [];

    if (!groupsByKind) return defaultGroups;
    if (whitelist && excludeDynamicGroups) return whitelist;

    if (whitelist && !excludeDynamicGroups) {
        defaultGroupBlacklist = defaultGroups.filter((group) => !whitelist.includes(group));
    }
    if (editableOnly) defaultGroupBlacklist.push('auto');
    defaultGroupBlacklist.push('pendo'); // never include `pendo` group in whitelist

    let metadataWhitelist;
    if (groupsArray) {
        metadataWhitelist = uniq(groupsByKind).filter((group) => !defaultGroupBlacklist.includes(group));
    } else {
        const schemaResponses = groupsByKind.map((group) => Object.keys(group));
        metadataWhitelist = schemaResponses.reduce((whitelist, schemaGroups) => {
            if (!isEmpty(schemaGroups)) {
                schemaGroups.forEach((group) => {
                    const eligableForWhitelist = !whitelist.includes(group) && !defaultGroupBlacklist.includes(group);

                    return eligableForWhitelist ? whitelist.push(group) : whitelist;
                });
            }

            return whitelist;
        }, []);
    }

    if (!includeTrackEventData) {
        metadataWhitelist = metadataWhitelist.filter((list) => list !== 'event');
    }

    return metadataWhitelist;
}

export async function getAvailableMetadataColumns () {
    const metadataStore = useMetadataStore();
    await metadataStore.loadAll();

    const metadata = formatMetadataForRules(metadataStore.metadataList);
    let options = buildRules(metadata);

    if (!hasSubscriptionFlag('usesSubaccounts')) {
        options = options.filter((rule) => !rule.id.includes('parentAccount'));
    }

    return options.map((option) => {
        const iconType = getOptionIconType(option);

        return {
            ...option,
            icon: {
                type: iconType,
                size: 14,
            },
        };
    });
}

export function generateColumnConfig (column) {
    let formatter = (row) => {
        if (!has(row, column.prop)) return null;

        return formatValueForSchema(row[column.prop], column.schema);
    };

    if (column.formatter) {
        formatter = column.formatter;
    }

    return {
        ...column,
        prop: column.prop,
        label: column.label,
        sortable: true,
        allowReorder: true,
        width: column.schema === 'time' ? 260 : undefined,
        formatter,
    };
}

export function buildRules (rules, includeProductUsage = true) {
    const productUsageRules = [
        { id: uuid(), name: $t('common.Page'), schema: 'number', type: 'page', kind: 'visitor' },
        { id: uuid(), name: $t('common.Feature'), schema: 'number', type: 'feature', kind: 'visitor' },
        { id: uuid(), name: $t('common.Guide'), schema: 'number', type: 'guide', kind: 'visitor' },
        { id: uuid(), name: $t('common.Poll Response'), schema: 'poll', type: 'poll', kind: 'visitor', field: 'response' },
    ];

    return rules
        .filter(
            (rule) =>
                rule.field !== 'visitor.auto.accountid' &&
                rule.field !== 'visitor.auto.id' &&
                rule.field !== 'visitor.auto.accountids',
        )
        .concat(
            ...[
                ...(includeProductUsage ? productUsageRules : []),
                {
                    id: 'visitor_eventCount',
                    name: $t('common.Number of Events'),
                    schema: 'aggregation',
                    type: 'eventCount',
                    fact: 'eventCount',
                    kind: 'visitor',
                },
                {
                    id: 'visitor_daysActive',
                    name: $t('common.Number of Days Active'),
                    schema: 'aggregation',
                    type: 'daysActive',
                    fact: 'daysActive',
                    kind: 'visitor',
                },
                {
                    id: 'visitor_eventTime',
                    name: $t('common.Time on Site (minutes)'),
                    schema: 'aggregation',
                    type: 'eventTime',
                    fact: 'eventTime',
                    kind: 'visitor',
                },
            ],
        );
}

export function formatMetadataForRules (metadata) {
    const groups = metadata.map((schema) => schema.group);
    const whitelistedGroups = getWhitelistedGroups(groups, { editableOnly: false, groupsArray: true });

    return metadata
        .filter((schema) => whitelistedGroups.includes(schema.group))
        .map((schema) => {
            const { group, kind, field, DisplayName, Type } = schema;

            return {
                kind,
                field: `${kind}.${group}.${field}`,
                id: `${kind}_${group}_${field}`,
                type: `${kind}.${field}`,
                name: DisplayName || field,
                schema: Type || 'string',
            };
        });
}

export function getOptionIconType (option) {
    if (isProductOption(option)) {
        return option.type;
    }

    return option.kind === 'replay' ? 'play-circle' : option.kind;
}

export function isProductOption (option) {
    return ['page', 'feature', 'poll', 'guide'].includes(option.type);
}

export function getTagHexRGB (tagType) {
    let rgb;
    switch (tagType) {
        case 'agent':
        case 'salesforce':
        case 'segmentio':
        case 'custom':
        case 'pendo_hubspot':
        case 'sfdc_user':
            rgb = METADATA_GROUP_COLORS[tagType];
            break;
        default:
            rgb = METADATA_GROUP_COLORS.unknown;
            break;
    }

    return rgb;
}

export function getDisplayName (group, field) {
    let displayName = group;
    if (group === 'pendo_hubspot') {
        displayName = 'HubSpot';
    } else if (group === 'pendo_predict') {
        displayName = 'Predict';
    } else if (group === 'sfdc_user') {
        displayName = 'Salesforce User Metadata';
    } else if (group === 'segmentio') {
        displayName = 'Segment.io';
    } else if (group.includes('pendo_')) {
        displayName = `${group.split('_')[1]} integration`;
    } else if (group === 'custom' && field && field.match(/[^a-zA-Z0-9_]/g)) {
        return;
    } else if (group === 'agent') {
        displayName = 'SDK';
    }

    return displayName;
}
